import React, { useCallback, useEffect, useMemo, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PageHeader, Tag } from "@/components/Layout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import {
  KeyRound, Plus, Pencil, Trash2, Eye, EyeOff, Search, Upload, Download,
  Share2, ExternalLink, Loader2, Copy, RefreshCw,
} from "lucide-react";
import { toast } from "sonner";
import { formatDate } from "@/lib/dateFormat";
import useBulkSelection from "@/hooks/useBulkSelection";

export default function PasswordVault() {
  const { user } = useAuth();
  const [rows, setRows] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [editing, setEditing] = useState(null);
  const [revealed, setRevealed] = useState({}); // {entryId: plaintext}
  const [revealing, setRevealing] = useState(null);
  const [importing, setImporting] = useState(false);
  const [sharingFor, setSharingFor] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [{ data }, { data: udata }] = await Promise.all([
        api.get("/vault", { params: { q: q || undefined } }),
        api.get("/users").catch(() => ({ data: [] })),
      ]);
      setRows(data || []);
      setUsers((udata || []).filter((u) => u.id !== user?.id && u.role !== "client" && u.role !== "vendor"));
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setLoading(false); }
  }, [q, user?.id]);

  useEffect(() => { load(); }, [load]);

  const filtered = useMemo(() => rows, [rows]);

  const bulk = useBulkSelection({
    collection: "password_vault",
    ids: filtered.map((r) => r.id),
    onDone: load,
  });

  const reveal = async (row) => {
    if (revealed[row.id]) {
      const next = { ...revealed };
      delete next[row.id];
      setRevealed(next);
      return;
    }
    setRevealing(row.id);
    try {
      const { data } = await api.post(`/vault/${row.id}/reveal`);
      setRevealed({ ...revealed, [row.id]: data.password });
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setRevealing(null); }
  };

  const copyPassword = async (row) => {
    try {
      const plain = revealed[row.id] || (await api.post(`/vault/${row.id}/reveal`)).data.password;
      await navigator.clipboard.writeText(plain || "");
      toast.success("Password copied · clears in 30s");
      setTimeout(() => { navigator.clipboard.writeText(""); }, 30000);
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const remove = async (row) => {
    if (!window.confirm(`Delete vault entry "${row.service_name}"? This cannot be undone.`)) return;
    try {
      await api.delete(`/vault/${row.id}`);
      toast.success("Deleted");
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const downloadTemplate = async () => {
    try {
      const resp = await api.get("/vault/template", { responseType: "blob" });
      const url = URL.createObjectURL(resp.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = "vault-template.xlsx";
      a.click();
      URL.revokeObjectURL(url);
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const onUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setImporting(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/vault/bulk-import", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      toast.success(`Imported ${data.created} entries${data.errors?.length ? ` · ${data.errors.length} row errors` : ""}`);
      load();
    } catch (err) { toast.error(formatApiError(err)); }
    finally {
      setImporting(false);
      e.target.value = "";
    }
  };

  return (
    <div className="min-h-full bg-slate-50 p-4 sm:p-8 max-w-7xl mx-auto fade-in">
      <PageHeader
        eyebrow="Registry · Vault"
        title="Password Vault"
        subtitle="Encrypted credential store. Passwords are AES-encrypted at rest. Owners, teammates you share with, and vault admins can reveal — every reveal is audit-logged."
        actions={(
          <div className="flex flex-wrap items-center gap-2">
            <Button variant="outline" className="rounded-sm" onClick={downloadTemplate} data-testid="vault-template">
              <Download size={13} className="mr-1.5" /> Template
            </Button>
            <label className="cursor-pointer inline-flex items-center gap-1.5 rounded-sm border border-slate-300 px-3 h-9 text-sm text-slate-700 hover:bg-slate-50">
              {importing ? <Loader2 size={13} className="animate-spin" /> : <Upload size={13} />} Bulk import
              <input type="file" accept=".xlsx" className="hidden" onChange={onUpload} data-testid="vault-import" disabled={importing} />
            </label>
            <Button
              onClick={() => setEditing({})}
              className="rounded-sm bg-[#F54800] hover:bg-[#D63E00] text-white"
              data-testid="vault-new"
            >
              <Plus size={13} className="mr-1.5" /> New entry
            </Button>
          </div>
        )}
      />

      <div className="space-y-4 mt-4">
        <div className="relative max-w-md">
          <Search size={13} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <Input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Search service, username, URL or tag…"
            className="rounded-sm pl-8 h-9 text-xs"
            data-testid="vault-search"
          />
        </div>

        <div className="card-flat overflow-hidden" data-testid="vault-table">
          {loading ? (
            <div className="p-10 text-center text-xs text-slate-400 inline-flex items-center gap-2 w-full justify-center">
              <Loader2 size={12} className="animate-spin" /> Loading…
            </div>
          ) : filtered.length === 0 ? (
            <div className="p-12 text-center text-sm text-slate-500">
              <KeyRound size={22} className="mx-auto mb-2 text-slate-300" />
              No entries yet. Click <span className="text-[#F54800] font-semibold">New entry</span> to add your first one.
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full text-xs">
                <thead className="bg-slate-50">
                  <tr>
                    {bulk.canBulk && (<th className="w-8 px-2 py-2.5"><bulk.HeaderCheckbox /></th>)}
                    {["Service", "Username", "Password", "URL", "Category", "Tags", "Owner", "Last rotated", ""].map((h) => (
                      <th key={h} className="text-left px-3 py-2.5 font-medium text-slate-500 uppercase tracking-wider text-[10px]">{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {filtered.map((r) => {
                    // Phase 84.13 — `is_owner_effective` is set by the backend
                    // to true for the real owner OR any vault_admin, so admins
                    // with edit access can share/delete every entry.
                    const isOwner = r.is_owner_effective ?? (r.owner_id === user?.id);
                    const isRealOwner = r.owner_id === user?.id;
                    const plain = revealed[r.id];
                    return (
                      <tr key={r.id} className={`hover:bg-slate-50 ${bulk.isSelected(r.id) ? "bg-orange-50/40" : ""}`} data-testid={`vault-row-${r.id}`}>
                        {bulk.canBulk && (<td className="w-8 px-2 py-2"><bulk.RowCheckbox id={r.id} /></td>)}
                        <td className="px-3 py-2 align-top">
                          <div className="font-medium text-slate-900" data-testid={`vault-service-${r.id}`}>{r.service_name}</div>
                          {r.notes && <div className="text-[10px] text-slate-500 mt-0.5 max-w-[220px] truncate" title={r.notes}>{r.notes}</div>}
                        </td>
                        <td className="px-3 py-2 font-mono text-slate-700 align-top">{r.username || <span className="text-slate-300">—</span>}</td>
                        <td className="px-3 py-2 align-top">
                          {r.has_password ? (
                            <div className="flex items-center gap-1.5">
                              <span className="font-mono text-[11px] text-slate-900 bg-yellow-50 px-1.5 py-0.5 rounded-sm min-w-[80px] inline-block">
                                {plain ? plain : "••••••••"}
                              </span>
                              <button
                                onClick={() => reveal(r)}
                                disabled={revealing === r.id}
                                className="p-1 rounded-sm text-slate-400 hover:text-[#F54800]"
                                title={plain ? "Hide" : "Reveal (audit-logged)"}
                                data-testid={`vault-reveal-${r.id}`}
                              >
                                {revealing === r.id ? <Loader2 size={11} className="animate-spin" /> : plain ? <EyeOff size={12} /> : <Eye size={12} />}
                              </button>
                              <button
                                onClick={() => copyPassword(r)}
                                className="p-1 rounded-sm text-slate-400 hover:text-slate-700"
                                title="Copy to clipboard (clears after 30s)"
                                data-testid={`vault-copy-${r.id}`}
                              >
                                <Copy size={12} />
                              </button>
                            </div>
                          ) : <span className="text-slate-300">—</span>}
                        </td>
                        <td className="px-3 py-2 align-top">
                          {r.url ? (
                            <a href={r.url} target="_blank" rel="noreferrer" className="text-blue-600 hover:underline inline-flex items-center gap-1 max-w-[160px] truncate">
                              <ExternalLink size={10} /> {r.url.replace(/^https?:\/\//, "").slice(0, 22)}
                            </a>
                          ) : <span className="text-slate-300">—</span>}
                        </td>
                        <td className="px-3 py-2 align-top">{r.category || <span className="text-slate-300">—</span>}</td>
                        <td className="px-3 py-2 align-top">
                          <div className="flex flex-wrap gap-1">
                            {(r.tags || []).map((t) => <Tag key={t} tone="slate">{t}</Tag>)}
                            {(!r.tags || r.tags.length === 0) && <span className="text-slate-300">—</span>}
                          </div>
                        </td>
                        <td className="px-3 py-2 align-top">
                          <div className="text-slate-700">{r.owner_name}</div>
                          {!isRealOwner && !r.is_owner_effective && <div className="text-[10px] text-slate-400 mt-0.5">shared with you</div>}
                          {!isRealOwner && r.is_owner_effective && <div className="text-[10px] text-orange-600 mt-0.5">vault admin</div>}
                          {isRealOwner && r.shared_with_names?.length > 0 && (
                            <div className="text-[10px] text-slate-400 mt-0.5">→ {r.shared_with_names.length} other(s)</div>
                          )}
                        </td>
                        <td className="px-3 py-2 align-top text-slate-700">
                          {r.last_rotated_at ? formatDate(r.last_rotated_at) : <span className="text-slate-300">—</span>}
                        </td>
                        <td className="px-3 py-2 text-right whitespace-nowrap align-top">
                          <div className="inline-flex items-center gap-0.5">
                            {isOwner && (
                              <button
                                onClick={() => setSharingFor(r)}
                                className="p-1.5 text-slate-400 hover:text-blue-600"
                                title="Share with team"
                                data-testid={`vault-share-${r.id}`}
                              >
                                <Share2 size={12} />
                              </button>
                            )}
                            <button
                              onClick={() => setEditing(r)}
                              className="p-1.5 text-slate-400 hover:text-slate-700"
                              title="Edit"
                              data-testid={`vault-edit-${r.id}`}
                            >
                              <Pencil size={12} />
                            </button>
                            {isOwner && (
                              <button
                                onClick={() => remove(r)}
                                className="p-1.5 text-slate-400 hover:text-red-600"
                                title={isRealOwner ? "Delete" : "Delete (vault admin)"}
                                data-testid={`vault-delete-${r.id}`}
                              >
                                <Trash2 size={12} />
                              </button>
                            )}
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      {editing !== null && (
        <VaultEditDialog
          row={editing}
          users={users}
          onClose={() => setEditing(null)}
          onSaved={() => { setEditing(null); load(); }}
        />
      )}

      {sharingFor && (
        <ShareDialog
          row={sharingFor}
          users={users}
          onClose={() => setSharingFor(null)}
          onSaved={() => { setSharingFor(null); load(); }}
        />
      )}
      <bulk.ActionBar />
      <bulk.ConfirmDialog />
    </div>
  );
}

/* -------------------------------------------------------------------------- */

function VaultEditDialog({ row, users, onClose, onSaved }) {
  const isNew = !row?.id;
  const [form, setForm] = useState(() => ({
    service_name: row?.service_name || "",
    username: row?.username || "",
    password: "",
    url: row?.url || "",
    category: row?.category || "",
    notes: row?.notes || "",
    tags: (row?.tags || []).join(", "),
    last_rotated_at: row?.last_rotated_at ? row.last_rotated_at.slice(0, 10) : "",
    shared_with: row?.shared_with || [],
  }));
  const [showPassword, setShowPassword] = useState(false);
  const [saving, setSaving] = useState(false);

  const update = (k, v) => setForm((s) => ({ ...s, [k]: v }));

  const generatePassword = () => {
    const chars = "ABCDEFGHJKLMNPQRSTUVWXYZabcdefghijkmnpqrstuvwxyz23456789!@#$%^&*";
    let p = "";
    const cryptoObj = window.crypto || window.msCrypto;
    const arr = new Uint32Array(20);
    cryptoObj.getRandomValues(arr);
    for (let i = 0; i < 20; i++) p += chars[arr[i] % chars.length];
    update("password", p);
    setShowPassword(true);
  };

  const save = async () => {
    if (!form.service_name.trim()) { toast.error("Service name is required"); return; }
    setSaving(true);
    try {
      const payload = {
        service_name: form.service_name.trim(),
        username: form.username.trim(),
        url: form.url.trim() || null,
        category: form.category.trim() || null,
        notes: form.notes.trim() || null,
        tags: form.tags.split(",").map((t) => t.trim()).filter(Boolean),
        last_rotated_at: form.last_rotated_at || null,
      };
      // Only send password if user typed one (otherwise we keep the existing encrypted value)
      if (form.password) payload.password = form.password;
      if (isNew) payload.shared_with = form.shared_with;
      if (isNew) {
        await api.post("/vault", payload);
        toast.success("Entry created");
      } else {
        await api.patch(`/vault/${row.id}`, payload);
        toast.success("Updated");
      }
      onSaved();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setSaving(false); }
  };

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="rounded-sm max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="vault-edit-dialog">
        <DialogHeader>
          <DialogTitle className="font-display flex items-center gap-2">
            <KeyRound size={16} className="text-[#F54800]" />
            {isNew ? "New vault entry" : "Edit vault entry"}
          </DialogTitle>
          <DialogDescription className="text-xs">
            The password is encrypted with AES-128 (Fernet) at rest. Leave it blank to keep the current value unchanged.
          </DialogDescription>
        </DialogHeader>

        <div className="grid grid-cols-2 gap-3 mt-2">
          <Field label="Service / Account name" required>
            <Input value={form.service_name} onChange={(e) => update("service_name", e.target.value)} className="rounded-sm" placeholder="e.g. GitHub, AWS console" data-testid="vault-field-service" />
          </Field>
          <Field label="Username / Email">
            <Input value={form.username} onChange={(e) => update("username", e.target.value)} className="rounded-sm font-mono" placeholder="admin@analogydesign.co" data-testid="vault-field-username" />
          </Field>
          <Field label="Password" colSpan hint={isNew ? "" : "Leave blank to keep current value"}>
            <div className="flex gap-2">
              <Input
                type={showPassword ? "text" : "password"}
                value={form.password}
                onChange={(e) => update("password", e.target.value)}
                className="rounded-sm font-mono"
                placeholder={isNew ? "Enter or generate a strong password" : "••••••••"}
                data-testid="vault-field-password"
              />
              <Button type="button" variant="outline" className="rounded-sm" onClick={() => setShowPassword((s) => !s)} title={showPassword ? "Hide" : "Show"}>
                {showPassword ? <EyeOff size={13} /> : <Eye size={13} />}
              </Button>
              <Button type="button" variant="outline" className="rounded-sm" onClick={generatePassword} title="Generate 20-char strong password">
                <RefreshCw size={13} className="mr-1" /> Generate
              </Button>
            </div>
          </Field>
          <Field label="URL" colSpan>
            <Input value={form.url} onChange={(e) => update("url", e.target.value)} className="rounded-sm" placeholder="https://…" data-testid="vault-field-url" />
          </Field>
          <Field label="Category">
            <Input value={form.category} onChange={(e) => update("category", e.target.value)} className="rounded-sm" placeholder="e.g. SaaS, Banking, Internal" data-testid="vault-field-category" />
          </Field>
          <Field label="Last rotated">
            <Input type="date" value={form.last_rotated_at} onChange={(e) => update("last_rotated_at", e.target.value)} className="rounded-sm" data-testid="vault-field-rotated" />
          </Field>
          <Field label="Tags (comma-separated)" colSpan>
            <Input value={form.tags} onChange={(e) => update("tags", e.target.value)} className="rounded-sm" placeholder="engineering, ops" data-testid="vault-field-tags" />
          </Field>
          <Field label="Notes" colSpan>
            <Textarea value={form.notes} onChange={(e) => update("notes", e.target.value)} rows={3} className="rounded-sm" placeholder="Recovery email, security questions, etc." data-testid="vault-field-notes" />
          </Field>
        </div>

        <DialogFooter className="mt-3">
          <Button variant="outline" className="rounded-sm" onClick={onClose}>Cancel</Button>
          <Button onClick={save} disabled={saving} className="rounded-sm bg-[#F54800] hover:bg-[#D63E00] text-white" data-testid="vault-save">
            {saving ? <><Loader2 size={11} className="animate-spin mr-1.5" /> Saving…</> : "Save"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/* -------------------------------------------------------------------------- */

function ShareDialog({ row, users, onClose, onSaved }) {
  const [picked, setPicked] = useState(new Set(row?.shared_with || []));
  const [saving, setSaving] = useState(false);

  const toggle = (uid) => {
    const next = new Set(picked);
    if (next.has(uid)) next.delete(uid); else next.add(uid);
    setPicked(next);
  };

  const save = async () => {
    setSaving(true);
    try {
      const current = new Set(row?.shared_with || []);
      const toAdd = Array.from(picked).filter((u) => !current.has(u));
      const toRemove = Array.from(current).filter((u) => !picked.has(u));
      if (toAdd.length > 0) await api.post(`/vault/${row.id}/share`, { user_ids: toAdd });
      if (toRemove.length > 0) await api.post(`/vault/${row.id}/unshare`, { user_ids: toRemove });
      toast.success(`Sharing updated · ${picked.size} teammate(s) have access`);
      onSaved();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setSaving(false); }
  };

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="rounded-sm max-w-md max-h-[80vh] overflow-y-auto" data-testid="vault-share-dialog">
        <DialogHeader>
          <DialogTitle className="font-display flex items-center gap-2"><Share2 size={16} /> Share <span className="text-[#F54800]">{row.service_name}</span></DialogTitle>
          <DialogDescription className="text-xs">Pick teammates who can reveal this password. They can also edit the entry; only you can delete it.</DialogDescription>
        </DialogHeader>
        <div className="space-y-1 mt-3 max-h-[50vh] overflow-y-auto">
          {users.length === 0 ? (
            <div className="text-xs text-slate-500 py-4 text-center">No other teammates available.</div>
          ) : users.map((u) => (
            <label key={u.id} className="flex items-center gap-2 px-2 py-1.5 rounded-sm hover:bg-slate-50 cursor-pointer">
              <input
                type="checkbox"
                checked={picked.has(u.id)}
                onChange={() => toggle(u.id)}
                className="rounded-sm accent-[#F54800]"
                data-testid={`vault-share-user-${u.id}`}
              />
              <div className="flex-1">
                <div className="text-sm text-slate-900">{u.name}</div>
                <div className="text-[10px] text-slate-500">{u.email} · {u.role}</div>
              </div>
            </label>
          ))}
        </div>
        <DialogFooter>
          <Button variant="outline" className="rounded-sm" onClick={onClose}>Cancel</Button>
          <Button onClick={save} disabled={saving} className="rounded-sm bg-[#F54800] hover:bg-[#D63E00] text-white" data-testid="vault-share-save">
            {saving ? <><Loader2 size={11} className="animate-spin mr-1.5" /> Saving…</> : "Update sharing"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function Field({ label, hint, required, colSpan, children }) {
  return (
    <div className={colSpan ? "col-span-2" : ""}>
      <Label className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">
        {label}{required && <span className="text-[#F54800]"> *</span>}
      </Label>
      <div className="mt-1">{children}</div>
      {hint && <div className="text-[10px] text-slate-400 mt-1">{hint}</div>}
    </div>
  );
}
